import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-google-places-autocomplete';
import { toast } from 'react-toastify';

import DictionariesApi from '@api/dictionaries.api';

import Table from '@components/Table';
import InputLabel from '@mui/material/InputLabel';
import MainContentHeader from '@components/MainContentHeader';
import MoreActionsButton from '@components/Table/MoreActionsButton';
import Modal from '@components/Modal';
import Loader from '@components/Loader';


import getInstitutionsType from '@helpers/getInstitutionsType';
import { DictionaryT } from '@types/Dictionaries.t';
import stores from '@stores';

import * as commonS from '@styles/common';
import * as S from './styles';

export const headCells = [
  { title: '#', key: 'number', sortable: false, width: '4%' },
  { title: 'Adresse', key: 'address', sortable: false, width: '96%' },
  { title: '', key: 'actions', sortable: false }
];

export interface ApiOptions {
  language?: string;
  region?: string;
}

interface LatLng {
  lat: number;
  lng: number;
}

const MittelstufeScreen = () => {
  const location = useLocation();
  const { pathname } = location;
  const [modalOpen, setModalOpen] = useState(false);
  const [address, setAdress] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [currentInstitution, setCurrentInstitution] = useState(null);
  const institutionType = getInstitutionsType(pathname.replace(/\//, ''));

  const { dictionariesStore } = stores;
  const {
    data,
    isLoading,
    institution,
    setInstitution
  } = dictionariesStore;
  const {
    layoutStore: { setConfirmModal },
  } = stores;

  useEffect(() => {
    dictionariesStore.getInstitutionsList(institutionType);
  }, []);

  const handleOpenEditModal = (row: any) => {
    setIsEdit(true);
    setModalOpen(true);
    setCurrentInstitution(row);
  };

  const handleEditAddress = (e: any, row) => {
    geocodeByAddress(e?.label)
      .then((results) => getLatLng(results[0]))
      .then(({ lat, lng }) =>
        setCurrentInstitution({
          ...currentInstitution,
          address: e.label,
          placeId: e.value.place_id,
          location: {
            type: 'Point',
            coordinates: [lng, lat],
          },
        })
      );
  };

  const handleEdit = async () => {
    await DictionariesApi.edit(currentInstitution).then((e: any) =>
      toast.success('Mittelsufe was edited successfully', { autoClose: 3000 })
    );
    await dictionariesStore.getInstitutionsList(institutionType);
    setModalOpen(false);
  };

  useEffect(() => {
    if (address) {
      geocodeByAddress(address?.label)
        .then((results) => getLatLng(results[0]))
        .then(({ lat, lng }) => setAdress({ ...address, lat, lng }));
    }
  }, [address?.label]);

  const handleAddNew = async () => {
    setModalOpen(false);
    await DictionariesApi.add({
      ...institution,
      type: institutionType,
      address: address?.label,
      coordinates: address?.lng ? [address.lng, address.lat] : undefined,
    }).then((e: any) =>
      toast.success('The Mittelsufe was added successfully', { autoClose: 3000 })
    );
    await setInstitution({});
    await dictionariesStore.getInstitutionsList(institutionType);
    setAdress(null);
  };

  const onDelete = (id: string) => {
    setConfirmModal({
      type: 'warning',
      open: true,
      title: 'Element wirklich löschen?',
      positiveCallback: async () => {
        await DictionariesApi.delete(id).then((e: any) =>
          toast.success('The Mittelsufe was deleted successfully', { autoClose: 3000 })
        );
        await dictionariesStore.getInstitutionsList(institutionType);
      },
      positiveButtonText: 'Ok',
      negativeButtonText: 'Cancel',
    });
  };

  const getFormattedRows = () => data.map((row: DictionaryT, index: number) => ({
    id: row?._id,
    number: <strong>{index + 1}</strong>,
    address: row?.address,
    actions: <MoreActionsButton isEdit onEdit={() => handleOpenEditModal(row)} onDelete={() => onDelete(row?._id)} />,
  }));

  return (
    <commonS.MainBody>
      <MainContentHeader title="Mittelstufe" buttonTitle="Hinzufügen" onButtonClick={() => setModalOpen(true)} />
      <Table
        headRows={headCells}
        bodyRows={getFormattedRows()}
        sorting={{
          sort: '',
          onChangeSorting: () => alert('SortChange')
        }}
      />
      <Modal
        modalOpen={modalOpen}
        isEdit={isEdit}
        onClose={() => {
          setModalOpen(false);
          setIsEdit(false);
        }}
        onConfirm={isEdit ? handleEdit : handleAddNew}
        setModalOpen={setModalOpen}
        title="Mittelstufe"
      >
        <S.FormItem>
          <InputLabel htmlFor='address' shrink={false}>Adresse</InputLabel>
          <GooglePlacesAutocomplete
            apiOptions={{ language: 'de', region: 'ch' }}
            autocompletionRequest={{
              componentRestrictions: {
                country: ['ch']
              }
            }}
            selectProps={{
              value: isEdit ? {
                label: currentInstitution?.address,
                value: {
                  place_id: currentInstitution?.placeId
                }
              } : address,
              onChange: isEdit ? (e) => handleEditAddress(e, currentInstitution) : setAdress,
              styles: {
                input: (provided: any) => ({
                  ...provided,
                  color: '#778097'
                }),
                control: (provided: any, state: any) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#4F77DE' : 'rgba(102, 102, 102, 0.35)',
                  outline: 'none',
                  boxShadow: 'none',
                  borderRadius: 8,
                  padding: '2.5px 5px'
                }),
                option: (provided: any) => ({
                  ...provided,
                  color: '#778097'
                }),
                singleValue: (provided: any) => ({
                  ...provided,
                  color: '#778097'
                }),
              }
            }}
          />
        </S.FormItem>
      </Modal>

      {isLoading && <Loader filled={false} />}
    </commonS.MainBody>
  );
};

export default observer(MittelstufeScreen);

